import { useState, useEffect, useRef, KeyboardEvent, FocusEvent, MouseEvent as ReactMouseEvent } from 'react'
import {Check, X, Pencil} from 'lucide-react'
import { getCachedText, saveInlineText } from '@/hooks/useSiteContent'
import { useEditMode } from '@/hooks/useEditMode'
import { useAuth } from '@/hooks/useAuth'

interface EditableTextProps {
  pageKey: string
  sectionKey: string
  fallback: string
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3'
  className?: string
  multiline?: boolean
  contentType?: 'title' | 'subtitle' | 'paragraph' | 'button' | 'list' | 'card'
}

// Editable text: renders the current cached value. When the site is in
// front-end edit mode (admin only), clicking the text turns it into an
// inline editable field. Saving persists directly to site_content.
export default function EditableText({
  pageKey,
  sectionKey,
  fallback,
  as = 'span',
  className,
  multiline = false,
  contentType = 'paragraph',
}: EditableTextProps) {
  const [, setTick] = useState(0)
  const { isAdmin } = useAuth()
  const { editMode } = useEditMode()
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const ref = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    const handler = () => setTick((n) => n + 1)
    window.addEventListener('site-content-updated', handler)
    return () => window.removeEventListener('site-content-updated', handler)
  }, [])

  const text = getCachedText(pageKey, sectionKey, fallback)
  const canEdit = isAdmin && editMode

  const startEditing = () => {
    if (!canEdit || isEditing) return
    setDraft(text)
    setIsEditing(true)
    setTimeout(() => ref.current?.focus(), 0)
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setDraft('')
  }

  const commit = async () => {
    const value = ref.current?.innerText ?? draft
    const trimmed = value.replace(/\u00A0/g, ' ').trimEnd()
    if (trimmed === text) {
      setIsEditing(false)
      return
    }
    setSaving(true)
    try {
      await saveInlineText(pageKey, sectionKey, trimmed || fallback, contentType)
      setIsEditing(false)
    } catch (err) {
      console.error('Impossible d\'enregistrer le texte:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditing()
    } else if (e.key === 'Enter' && !multiline && !e.shiftKey) {
      e.preventDefault()
      commit()
    }
  }

  const handleBlur = (e: FocusEvent<HTMLSpanElement>) => {
    // Avoid closing when clicking the save/cancel buttons
    if (e.relatedTarget && (e.relatedTarget as HTMLElement).closest('[data-editable-controls]')) {
      return
    }
    commit()
  }

  if (!canEdit) {
    const Tag = as as any
    return <Tag className={className}>{text}</Tag>
  }

  if (isEditing) {
    return (
      <span className="relative inline-block group/edit w-full align-top">
        <span
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className={`${className || ''} outline-none border-b-2 border-dashed border-[#c9a96e] bg-[#c9a96e]/10 px-1 -mx-1 rounded-sm cursor-text inline-block w-full whitespace-pre-line`}
        >
          {draft}
        </span>
        <span
          data-editable-controls
          className="absolute -top-9 left-0 flex items-center gap-1 bg-[#1a1a1a] border border-[#c9a96e]/40 rounded px-1.5 py-1 shadow-lg z-50 whitespace-nowrap"
        >
          <button
            type="button"
            tabIndex={-1}
            onClick={commit}
            disabled={saving}
            className="p-1 text-[#c9a96e] hover:text-[#e4c891] transition-colors disabled:opacity-50"
            aria-label="Enregistrer"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            onClick={cancelEditing}
            disabled={saving}
            className="p-1 text-[#a8a39e] hover:text-[#f5f1ea] transition-colors disabled:opacity-50"
            aria-label="Annuler"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      </span>
    )
  }

  const Tag = as as any
  return (
    <Tag
      onClick={(e: ReactMouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        startEditing()
      }}
      className={`${className || ''} group/editable relative cursor-pointer transition-all duration-200 rounded-sm outline-dashed outline-1 outline-transparent hover:outline-[#c9a96e]/50 hover:bg-[#c9a96e]/[0.06]`}
      title="Cliquer pour modifier"
    >
      {text}
      <Pencil className="inline-block w-3 h-3 ml-1.5 mb-1 text-[#c9a96e]/40 opacity-0 group-hover/editable:opacity-100 transition-opacity" />
    </Tag>
  )
}
